// Pure segment builders for PartsMap (no React) — unit-testable.
// Segment kinds: done | active | now | queued | failed (+ next: bool).

/**
 * Segments for the MODAL.
 *  - upload partial: first k = 'now', rest = 'queued'   (no per-part data yet)
 *  - upload k0:      all = 'queued'
 *  - resume:         real per-part states; the next k pending become 'now'
 *
 * TANDA I (B2) — LA MISMA PARTE ROJA, EN LA OTRA BARRA.
 * El remate F le dio a `buildCardSegments` su rama `error` -> `failed` (abajo, R-19) y
 * este builder se quedó sin ella: en `resume` una parte en `error` no casaba con ninguna
 * de las dos primeras ramas y caía al `else` POSICIONAL, que da por hecho "esto es una
 * pendiente". El disfraz salía de dos colores según dónde cayera:
 *   - fallida DESPUÉS de agotarse los k slots -> 'queued', GRIS: el mismo bug que la
 *     tarjeta tenía, idéntico, en el modal que se abre desde esa misma tarjeta;
 *   - fallida DENTRO de los k slots -> 'now', NARANJA + aria "se procesa ahora" (falso),
 *     y además CONSUME un slot, degradando a 'queued' a la pendiente que sí iba a correr.
 * Con la rama, la fallida se pinta fallida y `nowLeft` sólo lo gastan las pendientes de
 * verdad — que es lo que `k` siempre significó (`affordability` cuenta pendientes).
 *
 * `next` no se toca, igual que en la tarjeta: `next_chunk_index` sale de `parts[0]` de la
 * lista de PENDIENTES (documents.py:2149), así que jamás apunta a una parte en `error`.
 * La asimetría del excluido (aquí 'now', en la tarjeta 'active') es la correcta: cada
 * barra excluye el kind que ELLA usa para "esto arranca ya", y ninguna de las dos
 * excluye 'failed' — inalcanzable en ambas por la misma razón.
 */
// ajustes-1-3-0 (Bloque A2, #499): `overflow` son las partes del FINAL que no caben en
// ningun lado (ni ahora ni en Pendientes) y hay que volver a subir: en la subida con
// series7 se pintan como el tercer tramo (`overflow`), nunca como guardadas. Sin el (o en
// cero, o al reanudar) todo es byte por byte como antes.
export function buildModalSegments({ n, k, mode, parts, nextChunkIndex, overflow = 0 }) {
  if (mode === 'resume' && Array.isArray(parts) && parts.length) {
    let nowLeft = k;
    return parts.map((p) => {
      const st = p.status;
      let kind;
      if (st === 'ready' || st === 'partial') kind = 'done';
      else if (st === 'processing') kind = 'active';
      else if (st === 'error') kind = 'failed';
      else if (nowLeft > 0) { kind = 'now'; nowLeft -= 1; }   // next k pending -> process now
      else kind = 'queued';
      return { kind, index: p.chunk_index, next: p.chunk_index === nextChunkIndex && kind !== 'done' && kind !== 'now' };
    });
  }
  const desborde = Number.isFinite(overflow) && overflow > 0 ? Math.min(overflow, Math.max(0, n - k)) : 0;
  return Array.from({ length: n }, (_, i) => ({
    index: i + 1,
    kind: i < k ? 'now' : (i >= n - desborde ? 'overflow' : 'queued'),
    next: false,
  }));
}

/**
 * Segments for the /pending CARD from the live per-part `parts` array.
 *
 * R-19 (remate F) — LA PARTE ROJA QUE NO SE PINTABA DE ROJO.
 * `PartsMap` tiene desde siempre un `kind: 'failed'` (rojo + ▲, PartsMap.js:30) y NADIE
 * lo producía: aquí una parte en `error` caía en el `else` y salía GRIS, idéntica a una
 * pendiente. O sea que la barrita no señalaba la fallida — y lo único destacado en ella
 * era el filo naranja de `next`, que marca la primera parte PENDIENTE (la 1). Con la
 * fallida al final, el usuario leía ese filo de la posición 1 como "la rota es ésta".
 *
 * NO ERA UN PROBLEMA DE ORDEN: el orden lo fija el backend (`.sort("chunk_index", 1)`,
 * routes/documents.py:2127) y aquí se respeta tal cual — el segmento i ES la parte i.
 * Lo que faltaba era el color. Con esta rama, con [3,3,3,2] y la 4 fallida, el CUARTO
 * segmento sale rojo y el filo naranja se queda donde le toca, en el primero.
 *
 * `next` no se toca: `next_chunk_index` sale de `parts[0]` de la lista de PENDIENTES
 * (documents.py:2149), así que jamás apunta a una parte en `error`.
 */
export function buildCardSegments(parts = [], nextChunkIndex) {
  return parts.map((p) => {
    const st = p.status;
    let kind;
    if (st === 'ready' || st === 'partial') kind = 'done';
    else if (st === 'processing') kind = 'active';
    else if (st === 'error') kind = 'failed';
    else kind = 'queued';
    return {
      kind,
      index: p.chunk_index,
      next: p.chunk_index === nextChunkIndex && kind !== 'done' && kind !== 'active',
    };
  });
}
